import { type JSX } from 'react';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';

import cardTabShapeRaw from '../assets/cardTabShape.svg?raw';
import { type PdfApontamentosPageData, type PdfApontamentosSection } from '../benchmarkingReport.types';
import { ReportPage } from '../components/ReportPage';
import { PDF_BORDER_RADIUS, PDF_FONT_WEIGHTS, PDF_FONTS, PDF_PAGE, PDF_SPACING, devOnly } from '../lib/pdfTokens';

// ---------------------------------------------------------------------------
// Tab shape asset — encoded once at module load
// ---------------------------------------------------------------------------

function svgToDataUri(svg: string): string {
  return `data:image/svg+xml;base64,${btoa(
    encodeURIComponent(svg).replace(/%([0-9A-F]{2})/g, (_, hex: string) => String.fromCharCode(parseInt(hex, 16)))
  )}`;
}

const CARD_TAB_SHAPE_URI = svgToDataUri(cardTabShapeRaw);

const DASHED_SEPARATOR_URI = svgToDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 471.28 1"><path stroke="#d9d9e3" stroke-dasharray="3 6" stroke-linecap="round" d="M.5.5h470.28"/></svg>`
);

// ---------------------------------------------------------------------------
// Constants — resolved from Elo v3 tokens
// ---------------------------------------------------------------------------

const TEXT_PRIMARY = '#25252A';
const TEXT_SECONDARY = '#5E5E69';
const CARD_BG = '#FFFFFF';
const CARD_BORDER = '#D9D9E3';

// Improvements (warning) — yellow.light.1100 / yellow.light.100
const IMPROVEMENTS_BAR_COLOR = '#956D00';
const IMPROVEMENTS_BG = '#FFF5DB';
const IMPROVEMENTS_TEXT = '#5C4500'; // yellow.light.1300 — readable on yellow bg

// Strengths (success) — mint.light.1000 / mint.light.100
const STRENGTHS_BAR_COLOR = '#007A5C';
const STRENGTHS_BG = '#DAFBF3';
const STRENGTHS_TEXT = '#053D30'; // mint.light.1300 — readable on green bg

// Icon backgrounds (softest alpha)
const IMPROVEMENTS_ICON_BG = '#FFF5DB';
const STRENGTHS_ICON_BG = '#DAFBF3';

// ---------------------------------------------------------------------------
// Fallback analysis texts (used when API doesn't return mensagem fields)
// Keyed by axis name — matches what the API returns in eixos[].nome
// ---------------------------------------------------------------------------

const FALLBACK_IMPROVEMENTS: Record<string, string> = {
  'Língua portuguesa':
    'A análise dos apontamentos em Língua Portuguesa indica que as principais dificuldades dos alunos estão concentradas em aspectos gramaticais (especialmente no uso da vírgula) e ortográficos, como grafia das palavras e acentuação, e não em estruturas sintáticas mais complexas. Nesse cenário, a prática de reescrita pode ser uma estratégia eficaz, pois ajuda o aluno a identificar os desvios com mais clareza e a corrigi-los de forma mais consciente.',
  'Tema e tipo textual':
    'A principal dificuldade dos alunos está relacionada ao uso do repertório: há alta recorrência de repertório não legitimado e repertório limitado à coletânea, indicando que muitos textos até dialogam com o tema, mas não mobilizam referências válidas ou externas de forma adequada. Isso aponta a necessidade de fortalecer o trabalho com seleção, validação e ampliação de repertório, mais do que com o entendimento do tema ou do gênero em si.',
  'Argumentação e coerência':
    'O principal desafio está na profundidade do desenvolvimento argumentativo: a recorrência do apontamento "desenvolver mais" mostra que os alunos apresentam boas ideias, porém pouco aprofundadas. Além disso, trechos confusos indicam dificuldades na organização e no encadeamento do raciocínio ao longo do texto.',
  'Coesão e articulação':
    'Os apontamentos mostram que muitos textos ainda apresentam quebra de fluidez, sobretudo em função da repetição excessiva de palavras e do uso inadequado (ou da ausência) de conectivos entre frases e parágrafos. Isso indica que, embora os alunos saibam o que querem dizer, ainda têm dificuldade em encadear as ideias de forma progressiva para que o texto avance com clareza. Esses dados sugerem que a escola precisa reforçar estratégias de coesão, como variação vocabular e uso adequado de elementos coesivos.',
  'Proposta de intervenção':
    'A presença de elementos nulos na proposta de intervenção indica que ainda há redações em que os elementos da proposta não estão claros ou são muito genéricos. Isso sinaliza a necessidade de trabalhar em sala de aula a elaboração de propostas de intervenção que sejam completas e específicas, em que todos os elementos estejam claros e bem definidos.',
};

const FALLBACK_STRENGTHS: Record<string, string> = {
  'Tema e tipo textual':
    'Os dados mostram que muitos alunos já conseguem mobilizar repertórios socioculturais para embasar seus argumentos. Ainda assim, é importante trabalhar essa habilidade, pois parte dos alunos seleciona repertórios adequados, mas não os desenvolve de forma produtiva, o que prejudica o desempenho dos alunos nesta competência.',
  'Argumentação e coerência':
    'Os apontamentos positivos colocados nas redações indicam que alguns alunos já dominam estruturas argumentativas adequadas e conseguem construir um bom projeto de texto. Isso demonstra que a escola pode se basear nesses exemplos positivos de organização lógica e progressão temática para seguir aprofundando estas habilidades.',
  'Coesão e articulação':
    'Ao mesmo tempo, o volume de feedbacks positivos sobre conexão entre parágrafos e coordenação entre frases mostra que uma parte significativa dos alunos já consegue manter uma estrutura textual organizada. Esses estudantes demonstram noção de continuidade do texto e conseguem estabelecer relações básicas entre ideias, o que indica uma base de coesão já construída, que pode ser fortalecida com intervenções mais refinadas.',
  'Proposta de intervenção':
    'Os dados mostram que a maioria dos alunos consegue estruturar bem a proposta, destacando ação, agente e efeito, o que demonstra domínio do formato esperado no ENEM. A presença consistente desses elementos indica que os estudantes entendem a lógica da intervenção e sabem aplicá-la de forma funcional. O próximo avanço está em qualificar o detalhamento e o modo de execução, elevando propostas boas a propostas completas.',
};

// Generic fallback when no axis-specific text is available
const FALLBACK_GENERIC_IMPROVEMENTS =
  'A análise dos apontamentos indica oportunidades de melhoria em aspectos específicos desta competência. Recomenda-se trabalhar de forma direcionada as habilidades com maior frequência de ocorrência identificadas no gráfico.';

const FALLBACK_GENERIC_STRENGTHS =
  'Os pontos fortes identificados mostram que os alunos já demonstram domínio em alguns aspectos desta competência. Esses resultados positivos devem ser reconhecidos e utilizados como base para o desenvolvimento das demais habilidades.';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  // ── Outer card ────────────────────────────────────────────────────────────
  card: {
    flexGrow: 1,
    flexDirection: 'column',
    backgroundColor: CARD_BG,
    borderRadius: PDF_BORDER_RADIUS.md,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 2,
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: CARD_BORDER,
    marginHorizontal: -PDF_PAGE.cardBleed,
  },
  // ── Card header (title + subtitle) ────────────────────────────────────────
  cardHeader: {
    paddingHorizontal: PDF_SPACING[3],
    paddingTop: PDF_SPACING[3],
    paddingBottom: PDF_SPACING[4],
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: PDF_FONT_WEIGHTS.bold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    marginBottom: 2,
    lineHeight: 1.35,
  },
  cardSubtitle: {
    fontSize: 9,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
    lineHeight: 1.2,
  },

  // Tab label — absolutely positioned over the tab shape
  tabLabelWrapper: {
    position: 'relative',
    marginHorizontal: PDF_SPACING[3],
    marginBottom: -1,
  },
  tabShapeImage: {
    width: '100%',
  },
  tabLabel: {
    position: 'absolute',
    top: 0,
    left: PDF_SPACING[3],
    bottom: 0,
    justifyContent: 'center',
    maxWidth: 155,
  },
  tabLabelText: {
    fontSize: 10,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    lineHeight: 1.35,
  },

  // ── Inner card (wraps both sections) ─────────────────────────────────────
  innerCard: {
    flexGrow: 1,
    flexDirection: 'column',
    marginHorizontal: PDF_SPACING[3],
    marginBottom: PDF_SPACING[3],
    paddingHorizontal: PDF_SPACING[3],
    paddingTop: PDF_SPACING[3],
    borderBottomLeftRadius: PDF_BORDER_RADIUS.md,
    borderBottomRightRadius: PDF_BORDER_RADIUS.md,
    borderTopWidth: 0,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderStyle: 'solid',
    borderColor: CARD_BORDER,
  },

  // ── Section card (improvements / strengths) ───────────────────────────────
  sectionCard: {},
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PDF_SPACING[2],
    marginBottom: PDF_SPACING[2],
  },
  sectionIconBox: {
    width: 28,
    height: 28,
    borderRadius: PDF_BORDER_RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  sectionTitleBlock: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: PDF_FONT_WEIGHTS.semiBold,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_PRIMARY,
    lineHeight: 1.35,
  },
  sectionSubtitle: {
    fontSize: 8,
    fontWeight: 300,
    fontFamily: PDF_FONTS.sans,
    color: TEXT_SECONDARY,
    lineHeight: 1.3,
  },

  // Chart area — bleeds horizontally to card edges (negates innerCard paddingHorizontal)
  chartArea: {
    marginHorizontal: -PDF_SPACING[3],
    marginBottom: PDF_SPACING[2],
  },

  // Analysis text box
  analysisBox: {
    flexDirection: 'row',
    gap: PDF_SPACING[2],
    alignItems: 'flex-start',
    marginBottom: PDF_SPACING[3],
    borderRadius: PDF_BORDER_RADIUS.sm,
    paddingHorizontal: PDF_SPACING[3],
    paddingVertical: PDF_SPACING[2],
  },
  analysisIconBox: {
    width: 20,
    height: 20,
    borderRadius: PDF_BORDER_RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 3,
    flexShrink: 0,
    backgroundColor: CARD_BG,
  },
  analysisText: {
    flex: 1,
    fontSize: 8.5,
    fontWeight: 400,
    fontFamily: PDF_FONTS.sans,
    lineHeight: 1.5,
  },

  // Flex spacer — grows to fill remaining height, separator centered within it
  sectionSpacer: {
    flexGrow: 1,
    flexShrink: 0,
    minHeight: PDF_SPACING[2],
    justifyContent: 'center',
  },
  dashedSeparator: {
    height: 1,
  },
});

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type SectionCardProps = {
  section: PdfApontamentosSection;
  title: string;
  subtitle: string;
  iconUri?: string;
  iconBg: string;
  analysisBg: string;
  analysisTextColor: string;
  flagIconUri?: string;
  tabName: string;
  isStrengths: boolean;
};

function SectionCard({
  section,
  title,
  subtitle,
  iconUri,
  iconBg,
  analysisBg,
  analysisTextColor,
  flagIconUri,
  tabName,
  isStrengths,
}: SectionCardProps): JSX.Element {
  const fallbackMap = isStrengths ? FALLBACK_STRENGTHS : FALLBACK_IMPROVEMENTS;
  const fallbackGeneric = isStrengths ? FALLBACK_GENERIC_STRENGTHS : FALLBACK_GENERIC_IMPROVEMENTS;
  const analysisText = section.analise ?? devOnly(fallbackMap[tabName]) ?? devOnly(fallbackGeneric);
  return (
    <View style={styles.sectionCard}>
      {/* Section header — plain white, no background color */}
      <View style={styles.sectionHeader}>
        {iconUri ? (
          <View style={[styles.sectionIconBox, { backgroundColor: iconBg }]}>
            <Image
              src={iconUri}
              style={{ width: 20, height: 20 }}
            />
          </View>
        ) : null}
        <View style={styles.sectionTitleBlock}>
          <Text style={styles.sectionTitle}>{title}</Text>
          <Text style={styles.sectionSubtitle}>{subtitle}</Text>
        </View>
      </View>

      {/* Chart */}
      {section.chartUri ? (
        <View style={styles.chartArea}>
          <Image
            src={section.chartUri}
            style={{ width: 503 }}
          />
        </View>
      ) : null}

      {/* Analysis text — always shown, falls back to Figma text if API returns nothing */}
      <View style={[styles.analysisBox, { backgroundColor: analysisBg }]}>
        {flagIconUri ? (
          <View style={styles.analysisIconBox}>
            <Image
              src={flagIconUri}
              style={{ width: 14, height: 14 }}
            />
          </View>
        ) : null}
        <Text style={[styles.analysisText, { color: analysisTextColor }]}>{analysisText}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ApontamentosPageProps = {
  pageData: PdfApontamentosPageData;
  faceNeutralIconUri?: string;
  faceLoveIconUri?: string;
  flagIconUri?: string;
};

export function ApontamentosPage({
  pageData,
  faceNeutralIconUri,
  faceLoveIconUri,
  flagIconUri,
}: ApontamentosPageProps): JSX.Element {
  const hasImprovements = (pageData.improvements?.items.length ?? 0) > 0;
  const hasStrengths = (pageData.strengths?.items.length ?? 0) > 0;

  return (
    <ReportPage>
      <View style={styles.card}>
        {/* Card title */}
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Análise de bandeirinhas</Text>
          <Text style={styles.cardSubtitle}>
            Distribuição dos apontamentos, destacando pontos fortes e oportunidades de melhoria
          </Text>
        </View>

        {/* Tab shape with label */}
        <View style={styles.tabLabelWrapper}>
          <Image
            src={CARD_TAB_SHAPE_URI}
            style={styles.tabShapeImage}
          />
          <View style={styles.tabLabel}>
            <Text style={styles.tabLabelText}>{pageData.tabName}</Text>
          </View>
        </View>

        {/* Inner card — wraps both sections */}
        <View style={styles.innerCard}>
          {/* Improvements section */}
          {hasImprovements && pageData.improvements ? (
            <SectionCard
              section={pageData.improvements}
              title='Oportunidades de melhoria'
              subtitle='Critérios que apresentam maior necessidade de intervenção'
              iconUri={faceNeutralIconUri}
              iconBg={IMPROVEMENTS_ICON_BG}
              analysisBg={IMPROVEMENTS_BG}
              analysisTextColor={APONTAMENTOS_IMPROVEMENTS_LABEL_COLOR}
              flagIconUri={flagIconUri}
              tabName={pageData.tabName}
              isStrengths={false}
            />
          ) : null}

          {/* Flex spacer with centered separator — only when both sections present */}
          {hasImprovements && hasStrengths ? (
            <View style={styles.sectionSpacer}>
              <Image
                src={DASHED_SEPARATOR_URI}
                style={styles.dashedSeparator}
              />
            </View>
          ) : null}

          {/* Strengths section */}
          {hasStrengths && pageData.strengths ? (
            <SectionCard
              section={pageData.strengths}
              title='Pontos fortes'
              subtitle='Principais acertos identificados nas redações avaliadas'
              iconUri={faceLoveIconUri}
              iconBg={STRENGTHS_ICON_BG}
              analysisBg={STRENGTHS_BG}
              analysisTextColor={APONTAMENTOS_STRENGTHS_LABEL_COLOR}
              flagIconUri={flagIconUri}
              tabName={pageData.tabName}
              isStrengths={true}
            />
          ) : null}
        </View>
      </View>
    </ReportPage>
  );
}

// ---------------------------------------------------------------------------
// Exported color constants — used by buildBenchmarkingReportData
// ---------------------------------------------------------------------------

export const APONTAMENTOS_IMPROVEMENTS_BAR_COLOR = IMPROVEMENTS_BAR_COLOR;
export const APONTAMENTOS_STRENGTHS_BAR_COLOR = STRENGTHS_BAR_COLOR;
export const APONTAMENTOS_IMPROVEMENTS_TEXT_COLOR = IMPROVEMENTS_TEXT;
export const APONTAMENTOS_STRENGTHS_TEXT_COLOR = STRENGTHS_TEXT;
// Value label colors per Figma spec
export const APONTAMENTOS_IMPROVEMENTS_LABEL_COLOR = '#614700'; // Base-text-feedback-warning
export const APONTAMENTOS_STRENGTHS_LABEL_COLOR = '#00513D'; // Base-text-feedback-success
